import { Box, Button, Flex, FormField, InputSelectField, InputTextField } from '@tempmee/react';
import type { OptionValue } from '@tempmee/core';
import { useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useUpdateOffice, UpdateOfficeApiError } from '~/domain/UpdateOffice';
import { useAppSelector } from '~/hooks/useAppSelector';
import { fireGeneralOfficeInfoSubmitted } from '~/posthogs/events/Signup';
import { routes } from '~/routes';
import { AuthErrorBanner, useAuthFormSubmit } from '../../shared';
import type { ISignUpFormValues } from '../types';
import { OFFICE_TYPE_OPTIONS } from './constants';
import { OfficeAddressField } from './OfficeAddressField';
import { useOfficeDetailsHydration } from './useOfficeDetailsHydration';

const FORM_WIDTH = 704;
const CTA_WIDTH = 296;

const FIELD_ORDER: Array<
  keyof Pick<ISignUpFormValues, 'officeName' | 'officeAddress' | 'officeType' | 'officeSpecialty'>
> = ['officeName', 'officeAddress', 'officeType', 'officeSpecialty'];

export const OfficeDetailsForm = () => {
  const navigate = useNavigate();
  const { mutateAsync: updateOffice, isPending } = useUpdateOffice();
  const [placeId, setPlaceId] = useState<string | null>(null);
  useOfficeDetailsHydration();

  const specialtiesAndProcedures = useAppSelector(
    (state) =>
      (state.user.officeEducation as { specialtiesAndProcedures?: Record<string, string[]> } | null)
        ?.specialtiesAndProcedures,
  );

  const specialtyOptions = useMemo<OptionValue<string>[]>(
    () =>
      Object.keys(specialtiesAndProcedures ?? {})
        .filter((code) => code.startsWith('specialty_'))
        .map((code) => ({ label: code.split('_').slice(1).join('_'), value: code })),
    [specialtiesAndProcedures],
  );

  const {
    control,
    getValues,
    reset,
    setError,
    formState: { dirtyFields, errors },
  } = useFormContext<ISignUpFormValues>();

  const { submit, bannerVisible, bannerRef, serverMessage } = useAuthFormSubmit<ISignUpFormValues>({
    onValid: async ({ officeName, officeAddress, officeType, officeSpecialty }) => {
      fireGeneralOfficeInfoSubmitted();
      // dirtyFields tracks manual edits only (isDirty also flips on hydration
      // via setValue, so it's unreliable for a "did the user touch this?"
      // gate when the form is populated from BE).
      const userEdited = Object.keys(dirtyFields).length > 0;
      if (!userEdited && placeId === null) {
        navigate(routes.signUpRedesign.officeSetup);
        return;
      }
      if (dirtyFields.officeAddress && !placeId) {
        setError('officeAddress', {
          type: 'manual',
          message: 'Please select an address from the suggestions.',
        });
        return;
      }
      try {
        await updateOffice({ officeName, officeType, officeSpecialty, placeId, officeAddress });
        // Snapshot saved values as the new clean baseline so a resubmit
        // without changes short-circuits.
        reset(getValues());
        setPlaceId(null);
        navigate(routes.signUpRedesign.officeSetup);
      } catch (err) {
        if (err instanceof UpdateOfficeApiError && err.field) {
          setError(err.field, { type: 'server', message: err.message });
        }
        throw err;
      }
    },
  });

  const fieldErrorMessage = FIELD_ORDER.map((key) => errors[key])
    .filter((err): err is NonNullable<typeof err> => Boolean(err) && err?.type !== 'required')
    .map((err) => err.message as string | undefined)
    .find(Boolean);
  const bannerMessage = serverMessage ?? fieldErrorMessage;

  return (
    <Box style={{ width: FORM_WIDTH }}>
      <form onSubmit={submit} data-testid="office-details-form" noValidate>
        <Box
          ref={bannerRef}
          style={{ marginTop: bannerVisible ? -16 : 0, marginBottom: bannerVisible ? 8 : 0 }}
        >
          <AuthErrorBanner visible={bannerVisible} message={bannerMessage} />
        </Box>

        <Box>
          <FormField label="Office Name" htmlFor="officeName">
            <Controller
              name="officeName"
              control={control}
              rules={{
                required: 'Office name is required',
                maxLength: { value: 100, message: 'Office name must be 100 characters or less' },
              }}
              render={({ field, fieldState }) => (
                <InputTextField
                  data-cy="input-office-name"
                  id="officeName"
                  placeholder="Enter your office name"
                  maxLength={100}
                  isError={Boolean(fieldState.error)}
                  {...field}
                />
              )}
            />
          </FormField>
        </Box>

        <Box style={{ marginTop: 24 }}>
          <OfficeAddressField onPlaceIdChange={setPlaceId} />
        </Box>

        <Box style={{ marginTop: 24 }}>
          <FormField label="Office Type" htmlFor="officeType">
            <Controller
              name="officeType"
              control={control}
              rules={{ required: 'Office type is required' }}
              render={({ field, fieldState }) => (
                <div data-cy="select-office-type">
                  <InputSelectField<string>
                    options={OFFICE_TYPE_OPTIONS}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select Private Practice/DSO"
                    isError={Boolean(fieldState.error)}
                  />
                </div>
              )}
            />
          </FormField>
        </Box>

        <Box style={{ marginTop: 24 }}>
          <FormField label="Office Specialty" htmlFor="officeSpecialty">
            <Controller
              name="officeSpecialty"
              control={control}
              rules={{ required: 'Office specialty is required' }}
              render={({ field, fieldState }) => (
                <div data-cy="select-office-specialty">
                  <InputSelectField<string>
                    options={specialtyOptions}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select your primary specialty"
                    isError={Boolean(fieldState.error)}
                  />
                </div>
              )}
            />
          </FormField>
        </Box>

        <Flex justify="center" style={{ marginTop: 48 }}>
          <Box style={{ width: CTA_WIDTH }}>
            <Button
              data-cy="btn-office-details-continue"
              type="submit"
              variant="primary"
              size="lg"
              isLoading={isPending}
              style={{ width: '100%' }}
            >
              Show Me Who&rsquo;s Nearby
            </Button>
          </Box>
        </Flex>
      </form>
    </Box>
  );
};
